/**
 * Reading the customer sidebar out of a run's `facts` blob.
 *
 * Same contract as `detail.ts`: the blob is untyped on purpose, everything is
 * read defensively, and **what cannot be found is omitted rather than zeroed.**
 * The sidebar is the agent's evidence while they judge the draft — an invented
 * $0.00 order or a guarantee window that reads "0 days" would be evidence too,
 * of the wrong thing.
 */

export interface PanelOrder {
  orderId: string;
  createdAt: string | null;
  stage: string | null;
  paymentStatus: string | null;
  /** Three-valued: true = charged, false = never collected, null = unknown. */
  wasCharged: boolean | null;
  totalCents: number | null;
  fromSubscription: boolean;
}

export interface PanelSubscription {
  subscriptionId: string;
  itemDescription: string | null;
  quantity: number | null;
  frequency: string | null;
  nextShipmentAt: string | null;
}

export interface CustomerPanel {
  email: string | null;
  identitySource: string | null;
  orders: PanelOrder[];
  subscriptions: PanelSubscription[];
  guaranteeStatus: string | null;
  guaranteeWindowDays: number | null;
  hasRefundBlock: boolean;
  /** The run answered about an uncharged order because the customer raised it. */
  uncollectedPaymentOverride: boolean;
  /** True when intake never wrote its facts — the panel should say so. */
  empty: boolean;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function readCustomerPanel(raw: unknown): CustomerPanel {
  const facts = asRecord(raw) ?? {};

  const orders: PanelOrder[] = Array.isArray(facts.orders)
    ? facts.orders.flatMap((entry) => {
        const order = asRecord(entry);
        const orderId = asString(order?.orderId);
        if (!orderId) return [];
        return [
          {
            orderId,
            createdAt: asString(order?.createdAt),
            stage: asString(order?.currentStage),
            paymentStatus: asString(order?.paymentStatus),
            wasCharged: typeof order?.wasCharged === 'boolean' ? order.wasCharged : null,
            totalCents: asNumber(order?.totalCents),
            fromSubscription: order?.fromSubscription === true,
          },
        ];
      })
    : [];

  const subscriptions: PanelSubscription[] = Array.isArray(facts.subscriptions)
    ? facts.subscriptions.flatMap((entry) => {
        const subscription = asRecord(entry);
        const subscriptionId = asString(subscription?.subscriptionId);
        if (!subscriptionId) return [];
        return [
          {
            subscriptionId,
            itemDescription: asString(subscription?.itemDescription),
            quantity: asNumber(subscription?.quantity),
            frequency: asString(subscription?.frequency),
            nextShipmentAt: asString(subscription?.nextShipmentAt),
          },
        ];
      })
    : [];

  const conditions = asRecord(facts.conditions);

  return {
    email: asString(facts.resolvedEmail),
    identitySource: asString(facts.identitySource),
    orders,
    subscriptions,
    guaranteeStatus: asString(facts.guaranteeStatus),
    guaranteeWindowDays: asNumber(facts.guaranteeWindowDays),
    hasRefundBlock: conditions?.has_refund_block === true,
    uncollectedPaymentOverride: facts.uncollectedPaymentOverride === true,
    empty: Object.keys(facts).length === 0,
  };
}
